/**
 * Binding a clip to a character.
 *
 * Clips ship as their own GLBs, separate from the parts, so one retargeted clip set
 * can drive every character rather than being duplicated into each. A loaded
 * `AnimationGroup` targets the transform nodes that came with *its* file, so it has
 * to be rebound to the nodes of the character that should actually move -- matched by
 * bone name, which is safe because the pipeline writes clips and parts against the
 * same canonical skeleton.
 *
 * This is also the seam the layering system (M2) builds on: rebinding filtered to one
 * bone mask is what produces an upper-body and a lower-body group from one clip.
 */
import { AnimationGroup } from '@babylonjs/core/Animations/animationGroup';
import type { Scene } from '@babylonjs/core/scene';
import type { Skeleton } from '@babylonjs/core/Bones/skeleton';
import type { Node } from '@babylonjs/core/node';

import { loadSharedContainer } from './assetCache.ts';

import '@babylonjs/loaders/glTF/2.0';

export interface ClipRequest {
  /** Key the result is returned under. Distinct per request. */
  key: string;
  /** Animation name inside the bundle -- the pipeline writes the clip id. */
  name: string;
  /** Restrict the rebuilt group to these bones. */
  boneFilter?: (bone: string) => boolean;
  /**
   * Copy the curve data instead of referencing the bundle's.
   *
   * Sharing is the default and is what makes one parsed bundle drive a room full of
   * characters -- an `Animation` is immutable keyframe data. It stops being immutable
   * the moment something rewrites it in place, which is exactly what
   * `MakeAnimationAdditive` does, so additive clips ask for their own copy. Without
   * this an additive registered by the second character would be differenced twice.
   */
  clone?: boolean;
}

export interface BoundClip {
  group: AnimationGroup;
  /** Bones the clip actually drove on this character. */
  bound: number;
  /** Bone names the clip targets that this character does not have. */
  unmatched: string[];
  dispose(): void;
}

/** Bone name -> the transform node the skeleton drives for it. */
export function boneNodes(skeleton: Skeleton): Map<string, Node> {
  const nodes = new Map<string, Node>();
  for (const bone of skeleton.bones) {
    const node = bone.getTransformNode();
    if (node) nodes.set(bone.name, node);
  }
  return nodes;
}

/**
 * Loads a clip GLB and rebinds it onto `skeleton`.
 *
 * `boneFilter` restricts which bones the returned group drives -- pass one of the
 * layer masks to get a masked group. Omitted, the clip drives the whole body.
 */
export async function loadClip(
  scene: Scene,
  url: string,
  skeleton: Skeleton,
  options: { name?: string; boneFilter?: (bone: string) => boolean; clone?: boolean } = {},
): Promise<BoundClip> {
  // Shared and pinned: every character rebinds *this* parse's curves rather than
  // copying them, so the entry must outlive the characters that reference it.
  const container = await loadSharedContainer(scene, url, { hold: 'pin' });
  const source = container.animationGroups[0];
  if (!source) {
    throw new Error(`${url}: no animation group in the clip GLB`);
  }

  const targets = boneNodes(skeleton);
  const group = new AnimationGroup(options.name ?? source.name, scene);
  const unmatched: string[] = [];
  let bound = 0;

  for (const targeted of source.targetedAnimations) {
    const name = (targeted.target as { name?: string }).name;
    if (!name) continue;
    if (options.boneFilter && !options.boneFilter(name)) continue;

    const node = targets.get(name);
    if (!node) {
      if (!unmatched.includes(name)) unmatched.push(name);
      continue;
    }
    // The Animation object is immutable data; sharing it across characters is fine
    // and is what makes one loaded clip drive a room full of them.
    group.addTargetedAnimation(options.clone ? targeted.animation.clone() : targeted.animation, node);
    bound += 1;
  }

  group.normalize(source.from, source.to);

  return {
    group,
    bound,
    unmatched,
    dispose() {
      group.dispose();
    },
  };
}

/**
 * Loads one GLB holding many animations and rebinds the requested ones.
 *
 * A pack ships clips in bundles -- movement, idle, social, and one per stance -- so
 * the skeleton is stored once rather than once per clip. Registering ten clips must
 * therefore parse the file once, not ten times, which is what this exists for.
 */
export async function loadClipBundle(
  scene: Scene,
  url: string,
  skeleton: Skeleton,
  requests: ClipRequest[],
): Promise<Map<string, BoundClip>> {
  // Shared and pinned: a bundle is parsed once per scene however many characters
  // register from it, and its curves are referenced rather than copied -- so it is
  // never disposed while the scene lives.
  const container = await loadSharedContainer(scene, url, { hold: 'pin' });
  const targets = boneNodes(skeleton);
  const bound = new Map<string, BoundClip>();

  for (const request of requests) {
    const source = container.animationGroups.find((group) => group.name === request.name);
    if (!source) continue;

    const group = new AnimationGroup(request.key, scene);
    const unmatched: string[] = [];
    let count = 0;

    for (const targeted of source.targetedAnimations) {
      const name = (targeted.target as { name?: string }).name;
      if (!name) continue;
      if (request.boneFilter && !request.boneFilter(name)) continue;
      const node = targets.get(name);
      if (!node) {
        if (!unmatched.includes(name)) unmatched.push(name);
        continue;
      }
      group.addTargetedAnimation(
        request.clone ? targeted.animation.clone() : targeted.animation,
        node,
      );
      count += 1;
    }

    group.name = request.key;
    group.normalize(source.from, source.to);
    bound.set(request.key, {
      group,
      bound: count,
      unmatched,
      dispose: () => group.dispose(),
    });
  }

  return bound;
}
